/** Returns true when a media entry should be shown.
 *  Treats missing `display` as true for legacy API compat. */
function isVisible(m) {
    return m.display !== false;
}
const STORAGE_HOST_FALLBACK = 'https://api-storage.arkserver.arkturian.com';
/** Append the GLB compression preset — but only to storage-media URLs
 *  (a signed or external file_url must pass through untouched). */
function withGlbPreset(url, preset) {
    if (!url.includes('/storage/media/'))
        return url;
    if (/[?&]preset=/.test(url))
        return url;
    return url.includes('?') ? `${url}&preset=${preset}` : `${url}?preset=${preset}`;
}
export function buildMediaSequence(args) {
    const { knowledge, is3DCapable, resolve3DModelUrl, glbPreset = 'mobile' } = args;
    const items = [];
    // The annotated Schaubild is NOT in media[] — the wire shape carries
    // it as the top-level `storage_id` (the source image the annotation
    // anchors were curated against; media[] holds ADDITIONAL field
    // assets shot at other positions). Verified on knowledge 283
    // (Rotbuche, 2026-06-11). Synthesize it as the leading sequence item
    // and give it the annotations.
    // Synthesize only when the source image is genuinely absent from
    // media[] — matched by storage_id, NOT by kind: an explicit
    // illustration entry that is a DIFFERENT image must not suppress
    // the synthesis (its pins would land on the wrong picture).
    const sourceInMedia = knowledge.storage_id
        ? knowledge.media.some((m) => m.storage_id === knowledge.storage_id && isVisible(m))
        : false;
    if (knowledge.storage_id && !sourceInMedia) {
        // width-capped variant instead of the raw camera-resolution asset
        // — storage-api resizes server-side; multi-MB originals would
        // punish mobile for no visible gain at modal size.
        const base = knowledge.source_thumbnail_url
            ? knowledge.source_thumbnail_url.split('?')[0]
            : `${STORAGE_HOST_FALLBACK}/storage/media/${knowledge.storage_id}`;
        items.push({
            kind: 'illustration',
            url: `${base}?width=1600&format=jpg`,
            storage_id: knowledge.storage_id,
            thumbnail_url: knowledge.source_thumbnail_url ?? undefined,
            annotations: knowledge.annotations,
        });
    }
    // First: 2D media in API order (illustration, field_photos, video).
    // Skip display:false entries (curator-hidden) and non-renderable kinds
    // (audio, other). Both illustration AND field_photo carry annotations.
    // Video gets no annotation overlay (API has no time-coded pin anchors).
    for (const m of knowledge.media) {
        if (m.kind === '3d_model' || m.kind === 'audio' || m.kind === 'other')
            continue;
        if (!isVisible(m))
            continue;
        const baseUrl = m.file_url ?? m.url
            ?? `${m.storage_host ?? STORAGE_HOST_FALLBACK}/storage/media/${m.storage_id}`;
        // Annotations belong to the SOURCE image only — matched by
        // storage_id regardless of kind (an illustration that is a
        // different image must not inherit the anchors; review nit
        // 2026-06-11). Legacy items without a top-level storage_id keep
        // the old illustration heuristic.
        const carriesAnnotations = knowledge.storage_id
            ? m.storage_id === knowledge.storage_id
            : m.kind === 'illustration';
        items.push({
            kind: m.kind,
            url: baseUrl,
            storage_id: m.storage_id,
            thumbnail_url: m.thumbnail_url,
            annotations: carriesAnnotations ? knowledge.annotations : undefined,
        });
    }
    // Then: 3D model (if device-capable). Must also pass display filter.
    // Lazy URL so the slow link_id lookup doesn't block sequence-build.
    if (is3DCapable) {
        const glb = knowledge.media.find((m) => m.kind === '3d_model' && isVisible(m));
        if (glb) {
            const directUrl = withGlbPreset(glb.file_url ?? glb.url
                ?? `${glb.storage_host ?? STORAGE_HOST_FALLBACK}/storage/media/${glb.storage_id}`, glbPreset);
            items.push({
                kind: '3d_model',
                url: resolve3DModelUrl
                    ? () => resolve3DModelUrl(glb.storage_id)
                    : directUrl,
                storage_id: glb.storage_id,
                annotations: knowledge.annotations,
            });
        }
    }
    return items;
}
//# sourceMappingURL=buildMediaSequence.js.map